// src/components/Minihome/widgets/ReactionBar.jsx

import { useEffect, useMemo, useState } from 'react';
import { Box, Stack, IconButton, Tooltip, Typography, Snackbar, Popover, Button, Badge } from '@mui/material'; // ✅ Badge import 추가
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import { useUser } from '../../../contexts/UserContext';
import api from '../../../api/axios';

const ALL_TYPES = [
    { key: 'heart', label: '하트', Icon: FavoriteIcon },
    { key: 'thumb', label: '좋아요', Icon: ThumbUpAltIcon },
    { key: 'leaf', label: '리프', Icon: EnergySavingsLeafIcon },
];

export default function ReactionBar({ username, item }) {
    const { userInfo } = useUser();
    const isLoggedIn = !!userInfo;
    const isSelf = isLoggedIn && userInfo.username === username;

    const [reactionData, setReactionData] = useState({
        counts: { heart: 0, thumb: 0, leaf: 0 },
        my_today: null,
        enabled_types: ['heart', 'thumb', 'leaf'],
        display_mode: 'daily',
    });

    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const { displayMode, enabledTypes } = useMemo(() => {
        const config = item?.reactionConfig || {};
        return {
            displayMode: config.displayMode || 'daily',
            enabledTypes: config.enabledTypes || ['heart', 'thumb', 'leaf'],
        };
    }, [item]);

    const visibleTypes = useMemo(() => ALL_TYPES.filter(t => enabledTypes.includes(t.key)), [enabledTypes]);

    const handleOpenPopover = (event) => {
        if (!isLoggedIn || isSelf) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => setAnchorEl(null);

    const fetchSummary = useMemo(() => async () => {
        const { data } = await api.get(`/api/minihome/${encodeURIComponent(username)}/reactions/summary/`);
        setReactionData(data);
    }, [username]);

    useEffect(() => { fetchSummary().catch(() => { }); }, [fetchSummary]);

    const react = async (type) => {
        if (!isLoggedIn || isSelf || loading) return;
        setLoading(true);
        handleClosePopover();
        try {
            const { data } = await api.post(`/api/minihome/${encodeURIComponent(username)}/react/`, { type });
            setReactionData(prev => ({ ...prev, counts: data.summary, my_today: data.my_today }));
            setToast(reactionData.my_today === type ? '오늘 응원을 다시 선택했어요.' : '응원을 보냈어요!');
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.detail || '응원 처리에 실패했어요.';
            setToast(msg);
        } finally {
            setLoading(false);
        }
    };

    const totalReactions = useMemo(() => Object.values(reactionData.counts).reduce((sum, count) => sum + count, 0), [reactionData.counts]);

    const wrapperStyle = { p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 1 };

    // ✅ 표시할 응원이 1개일 때의 UI (수정된 부분)
    if (visibleTypes.length === 1) {
        const singleType = visibleTypes[0];
        const { key, label, Icon } = singleType;
        const count = reactionData.counts[key] ?? 0;
        const selected = reactionData.my_today === key;
        const disabled = loading || !isLoggedIn || isSelf;

        const title = !isLoggedIn
            ? '로그인 후 응원할 수 있어요'
            : isSelf
                ? '자기 미니홈에는 응원을 보낼 수 없어요'
                : (selected ? `${label} (오늘 선택됨)` : `${label} 응원 보내기`);

        return (
            <Box sx={wrapperStyle}>
                <Tooltip title={title}>
                    {/* ✅ Badge 컴포넌트로 아이콘을 감싸고, count를 badgeContent로 사용 */}
                    <Badge
                        badgeContent={count} // 숫자를 배지로 표시
                        color="primary" // 배지 색상
                        overlap="circular" // 배지 위치를 아이콘의 오른쪽 상단으로
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // 배지 위치 상세 조정
                        sx={{ // 배지 자체 스타일 (optional)
                            '.MuiBadge-badge': {
                                right: -3, // 아이콘에 더 가깝게
                                top: 3,
                                border: `2px solid white`, // 하얀색 테두리로 가독성 높임
                                padding: '0 4px',
                                height: 20, // 높이 조정
                                minWidth: 20, // 최소 너비 조정
                            }
                        }}
                    >
                        <IconButton
                            size="large" // 아이콘 크기를 크게
                            onClick={() => react(key)}
                            disabled={disabled}
                            sx={{
                                color: selected ? 'primary.main' : 'action.active', // 선택되면 강조 색상, 아니면 회색
                                // 기존의 불필요한 border, bgcolor 제거
                                // '&:hover': { bgcolor: selected ? 'primary.light' : 'action.hover' } // 호버 효과는 필요시 다시 추가
                            }}
                        >
                            <Icon fontSize="large" /> {/* 아이콘 크기도 large로 */}
                        </IconButton>
                    </Badge>
                </Tooltip>
                {/* ✅ 숫자를 더 이상 Typography로 따로 렌더링하지 않음 */}
                <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
            </Box>
        );
    }

    // ✅ 표시할 응원이 1개가 아닐 때 (0개 또는 여러 개)의 기존 UI
    const buttonText = displayMode === 'cumulative' ? `누적 응원 ${totalReactions}개` : `오늘 응원 ${totalReactions}개`;

    return (
        <Box sx={wrapperStyle}>
            <Tooltip title={!isLoggedIn ? '로그인 후 응원할 수 있어요' : isSelf ? '자신의 미니홈에는 응원할 수 없어요' : '응원하기'}>
                <span>
                    <Button variant="outlined" size="small" onClick={handleOpenPopover} disabled={!isLoggedIn || isSelf || visibleTypes.length === 0} sx={{ width: '100%' }}>
                        {visibleTypes.length === 0 ? '표시할 응원 없음' : buttonText}
                    </Button>
                </span>
            </Tooltip>

            <Popover open={open} anchorEl={anchorEl} onClose={handleClosePopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5 }}>
                    {visibleTypes.map(({ key, label, Icon }) => {
                        const selected = reactionData.my_today === key;
                        return (
                            <Tooltip key={key} title={(selected ? `${label} (오늘 선택됨)` : `${label} 응원 보내기`)}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => react(key)} disabled={loading} sx={{ border: 1, borderColor: selected ? 'primary.main' : 'divider', bgcolor: selected ? 'primary.light' : 'transparent', '&:hover': { bgcolor: selected ? 'primary.light' : 'action.hover' } }}>
                                        <Icon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2">{reactionData.counts[key] ?? 0}</Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Stack>
            </Popover>

            <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}